/**
 * FitnessAnalyticsPage - Comprehensive fitness progress analytics and insights
 */

import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  TrendingUp,
  Calendar,
  Dumbbell,
  RefreshCw,
  Sparkles,
  Download,
  Eye,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StatsSummary from '../components/fitness/StatsSummary';
import ProgressChart from '../components/fitness/ProgressChart';
import TrainingHeatmap from '../components/fitness/TrainingHeatmap';
import MuscleRadarChart from '../components/fitness/MuscleRadarChart';
import PRBoard from '../components/fitness/PRBoard';
import AIInsightsCard from '../components/fitness/AIInsightsCard';
import { useCoachStore } from '../stores/coachStore';
import { coachRelationships } from '../services/coachService';
import { subDays, format, parseISO, startOfDay, differenceInDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { getMuscleForExercise } from '../data/exercises';

// Normalize muscle group for chart display
// Maps detailed muscle groups to main categories
const normalizeMuscleGroup = (muscle) => {
  if (!muscle) return 'other';

  // Leg-related muscles -> legs
  if (['quadriceps', 'hamstrings', 'glutes', 'calves'].includes(muscle)) {
    return 'legs';
  }

  // Core/abs -> core
  if (['abdominals', 'abs'].includes(muscle)) {
    return 'core';
  }

  // Already normalized
  if (['chest', 'back', 'shoulders', 'biceps', 'triceps', 'legs', 'core'].includes(muscle)) {
    return muscle;
  }

  return 'other';
};

// Get muscle group for an exercise (inferred from exercise name)
const getMuscleGroup = (workout) => {
  // First try to infer from exercise name (most reliable)
  const inferredMuscle = getMuscleForExercise(workout.exercise_name);
  if (inferredMuscle) {
    return normalizeMuscleGroup(inferredMuscle);
  }

  // Fallback to database field (might be null)
  if (workout.muscle_group) {
    return normalizeMuscleGroup(workout.muscle_group);
  }

  return 'other';
};

const FitnessAnalyticsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('client');

  const [dateRange, setDateRange] = useState(365); // Last 1 year default
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Coach mode state
  const { viewingClientId, viewingClientInfo } = useCoachStore();
  const effectiveClientId = clientIdFromUrl || viewingClientId;

  // Load ALL workouts directly (bypass store's month filter)
  useEffect(() => {
    const loadAllWorkouts = async () => {
      setIsLoadingData(true);
      try {
        let data;
        if (effectiveClientId) {
          // Coach mode: load client's workouts
          data = await coachRelationships.getClientWorkouts(effectiveClientId, '2020-01-01', '2030-12-31');
        } else {
          // Normal mode: load own workouts
          const { fitness } = await import('../services/supabaseService');
          data = await fitness.getWorkouts('2020-01-01', '2030-12-31');
        }
        console.log('Analytics loaded workouts:', data?.length);
        setAllWorkouts(data || []);
      } catch (err) {
        console.error('Failed to load all workouts:', err);
        setAllWorkouts([]);
      } finally {
        setIsLoadingData(false);
      }
    };
    loadAllWorkouts();
  }, [effectiveClientId]);

  // Use allWorkouts instead of store workouts
  const workouts = allWorkouts;

  // Filter workouts by date range
  const filteredWorkouts = useMemo(() => {
    if (!workouts?.length) return [];

    const cutoffDate = subDays(new Date(), dateRange);
    return workouts.filter(w => {
      const workoutDate = parseISO(w.date);
      return workoutDate >= cutoffDate;
    });
  }, [workouts, dateRange]);

  // Calculate comprehensive stats
  const stats = useMemo(() => {
    if (!filteredWorkouts.length) return null;

    // Unique workout days
    const uniqueDays = new Set(filteredWorkouts.map(w => w.date));
    const totalDays = uniqueDays.size;

    // Find actual date range from data (first workout to today)
    const sortedDates = [...uniqueDays].sort();
    const firstWorkoutDate = parseISO(sortedDates[0]);
    const today = new Date();
    const actualDaysSpan = Math.max(1, differenceInDays(today, firstWorkoutDate) + 1);

    // Total volume
    const totalVolume = filteredWorkouts.reduce((sum, w) => {
      return sum + ((w.reps || 0) * parseFloat(w.weight || 0));
    }, 0);

    // Total sets
    const totalSets = filteredWorkouts.length;

    // Total reps
    const totalReps = filteredWorkouts.reduce((sum, w) => sum + (w.reps || 0), 0);

    // Unique exercises
    const uniqueExercises = new Set(filteredWorkouts.map(w => w.exercise_name));

    // Most frequent exercise
    const exerciseCounts = {};
    filteredWorkouts.forEach(w => {
      exerciseCounts[w.exercise_name] = (exerciseCounts[w.exercise_name] || 0) + 1;
    });
    const mostFrequent = Object.entries(exerciseCounts)
      .sort((a, b) => b[1] - a[1])[0];

    // Muscle group distribution (count sets per muscle group)
    const muscleGroups = {};
    filteredWorkouts.forEach(w => {
      const group = getMuscleGroup(w);
      muscleGroups[group] = (muscleGroups[group] || 0) + 1;
    });

    // Calculate streak (consecutive days)
    const sortedDays = [...uniqueDays].sort().reverse();
    let streak = 0;
    let currentDate = startOfDay(new Date());

    for (const dayStr of sortedDays) {
      const dayDate = startOfDay(parseISO(dayStr));
      const diff = differenceInDays(currentDate, dayDate);

      if (diff <= 1) {
        streak++;
        currentDate = dayDate;
      } else {
        break;
      }
    }

    // Average workouts per week - use actual data span, not selected range
    const actualWeeks = Math.max(1, actualDaysSpan / 7);
    const avgPerWeek = (totalDays / actualWeeks).toFixed(1);

    return {
      totalDays,
      totalVolume,
      totalSets,
      totalReps,
      uniqueExercises: uniqueExercises.size,
      mostFrequent: mostFrequent ? { name: mostFrequent[0], count: mostFrequent[1] } : null,
      muscleGroups,
      streak,
      avgPerWeek,
      actualDaysSpan, // Export for UI display
      firstWorkoutDate: sortedDates[0],
    };
  }, [filteredWorkouts, dateRange]);

  // Get personal records
  const personalRecords = useMemo(() => {
    if (!filteredWorkouts.length) return [];

    const prMap = {};
    filteredWorkouts.forEach(w => {
      const weight = parseFloat(w.weight || 0);
      if (weight > 0) {
        if (!prMap[w.exercise_name] || weight > prMap[w.exercise_name].weight) {
          prMap[w.exercise_name] = {
            exercise: w.exercise_name,
            weight,
            reps: w.reps,
            date: w.date,
            muscle_group: getMuscleGroup(w),
          };
        }
      }
    });

    return Object.values(prMap)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 10);
  }, [filteredWorkouts]);

  // Refresh data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      let data;
      if (effectiveClientId) {
        data = await coachRelationships.getClientWorkouts(effectiveClientId, '2020-01-01', '2030-12-31');
      } else {
        const { fitness } = await import('../services/supabaseService');
        data = await fitness.getWorkouts('2020-01-01', '2030-12-31');
      }
      setAllWorkouts(data || []);
    } catch (err) {
      console.error('Failed to refresh workouts:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Navigate back to FitTrack, preserving client param if in coach mode
  const handleBack = () => {
    if (effectiveClientId) {
      navigate(`/fittrack?client=${effectiveClientId}`);
    } else {
      navigate('/fittrack');
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Coach viewing mode banner */}
      {effectiveClientId && (
        <div className="bg-blue-600/20 border-b border-blue-500/30 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm">
            <Eye className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300">
              <span className="font-medium">{viewingClientInfo?.display_name || 'Öğrenci'}</span>
              <span className="text-blue-400/70">'nin analitiklerini görüntülüyorsunuz</span>
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-orange-500" />
                  Fitness Analytics
                  {effectiveClientId && (
                    <span className="text-sm font-normal text-gray-400">
                      ({viewingClientInfo?.display_name || 'Öğrenci'})
                    </span>
                  )}
                </h1>
                <p className="text-sm text-gray-400">
                  {dateRange >= 9999 ? 'Tüm zamanlar' : `Son ${dateRange} günlük`} ilerleme analizi
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Date Range Selector */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm"
              >
                <option value={30}>Son 30 Gün</option>
                <option value={90}>Son 90 Gün</option>
                <option value={180}>Son 6 Ay</option>
                <option value={365}>Son 1 Yıl</option>
                <option value={9999}>Tüm Zamanlar</option>
              </select>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {isLoadingData ? (
          <div className="flex items-center justify-center py-20">
            <Dumbbell className="w-8 h-8 animate-pulse text-orange-500" />
          </div>
        ) : !filteredWorkouts.length ? (
          <div className="text-center py-20">
            <Dumbbell className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-400 mb-2">Henüz veri yok</h2>
            <p className="text-gray-500">Antrenman kayıtları ekledikten sonra analizler burada görünecek.</p>
          </div>
        ) : (
          <>
            {/* Stats Summary Cards */}
            <StatsSummary stats={stats} dateRange={dateRange} />

            {/* AI Insights */}
            <AIInsightsCard
              workouts={filteredWorkouts}
              stats={stats}
              dateRange={dateRange}
            />

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Progress Chart */}
              <ProgressChart workouts={filteredWorkouts} />

              {/* Muscle Radar Chart */}
              <MuscleRadarChart muscleGroups={stats?.muscleGroups || {}} />
            </div>

            {/* Training Heatmap */}
            <TrainingHeatmap workouts={filteredWorkouts} dateRange={dateRange} />

            {/* Personal Records */}
            <PRBoard records={personalRecords} />
          </>
        )}
      </main>
    </div>
  );
};

export default FitnessAnalyticsPage;
